import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface SingleRoomSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const SingleRoomSection: React.FC<SingleRoomSectionProps> = ({
  room,
  onViewDetails,
  onQuickBook,
}) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#single-rooms`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our Single Rooms with comfortable accommodation and Alpine views`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.singleRoomBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.singleRoomBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.singleRoomBadges.bedding"),
      description: "Comfortable single bed",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.singleRoomBadges.bathrooms"),
      description: "Ensuite bathroom",
    },
  ];

  // Amenities for text badges
  const amenityBadges = [
    "Perfect for solo travelers",
    "With/without balcony; mountain, lake, or village views",
  ];

  return (
    <section
      id="single-rooms"
      className="py-16 bg-gradient-to-b from-white to-gray-50/50 relative overflow-hidden"
      data-oid="_frkn3x"
    >
      {/* Background Quadrilaterals */}
      <div
        className="absolute top-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="i1jy79i"
      ></div>
      <div
        className="absolute bottom-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="f8qur:m"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="_.gr74m"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid="wr.67.v">
          <div className="mb-4" data-oid="pkk1hqu">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="f2ncg.1"
            >
              {t("rooms.standardSingle")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid="odq4fvh"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="l14wugb"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="8k:kw2."
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="hstz2m:"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="df:06xl"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="g_7ru83"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="c5k.dg6"
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="3z5m9-z"
            >
              <Bed
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="xo8zlvn"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="xsly2f2"
              >
                {featureBadges[2].value}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Carousel Left (60%), Content Right (40%) */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[50%_50%] xl:grid-cols-[55%_45%] 2xl:grid-cols-[60%_40%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="31sbl.3"
        >
          {/* Left Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl"
            data-oid="l_bwo5l"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid="jiz-kq5"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid="e:zvjzq"
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={false}
                    rootMargin="200px"
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="5c9ck4p"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="3pm-byf"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="sugtaty" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="-c:d0tm"
                >
                  <ChevronRight className="w-5 h-5" data-oid="2:jlsvi" />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid="b:ww.2_"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="8l6pb90"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pr-4 lg:pr-6 xl:pr-8"
            data-oid="sgx-ftf"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid="okyf01u">
              <div className="mb-4" data-oid="mwth0sp">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="eg2:pq."
                >
                  {t("rooms.standardSingle")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="m.qlk58"
              >
                <div className="flex items-center gap-2" data-oid="64u_jkk">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="tlnzhpo"
                  />

                  <span className="text-sm text-gray-900" data-oid="8plv-_d">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="e1ukh4g">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="zns5zl5"
                  />

                  <span className="text-sm text-gray-900" data-oid="3a74i2-">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="cp_zl-.">
                  <Bed
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="8qt77--"
                  />

                  <span className="text-sm text-gray-900" data-oid="2s90r2_">
                    {featureBadges[2].value}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="kaduq1c">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid="0qu02au"
              >
                <span className="sm:hidden" data-oid="8ybtg0s">
                  Comfortable single rooms with essential amenities, perfect for
                  solo travelers or business guests. Choose between mountain
                  views of Eiger, Mönch & Jungfrau or Lake Thun views.
                </span>
                <span className="hidden sm:inline" data-oid="7:6plfv">
                  {t("rooms.singleRoomDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="_hlkd2z">
              {/* Perfect for solo travelers */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="9.6jflb"
              >
                <Users
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="wvdjurc"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="172h4v_"
                >
                  {t("rooms.singleRoomFeatures.perfectForSoloTravelers")}
                </span>
              </div>

              {/* With/without balcony views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="vh1gds2"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="rh8kt5g"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="xfim1m:"
                >
                  {t("rooms.singleRoomFeatures.balconyWithViews")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="1qlg-:-"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="p-2pv6z"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="i_0iohh"
                >
                  Free Wifi
                </span>
              </div>

              {/* Flatscreen TV */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="gc8u3e6"
              >
                <Tv
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="_q4rvho"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="9250.mj"
                >
                  Flatscreen TV
                </span>
              </div>

              {/* Alpine views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="xclx5d7"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="8t8aoma"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="e6ok51e"
                >
                  Alpine views
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="x3j:2xj"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="2fojrd4"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid=":kux186"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid="-x30n5l"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Single Room Section"
                data-oid="fz28gad"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="lii_2rl"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Single Rooms"
                data-oid="8rb74uv"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="ppmfxt8" />
                {t("common.share")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SingleRoomSection;
